import React from "react";
import { FiCheck, FiMoon, FiSun } from "react-icons/fi";
import { usePmsTheme } from "./PmsThemeContext";
import { translateReservationStatus } from "../utils/pmsLanguages";
import { isDemoMode } from "../demo/demoMode";
import { getPurchasingThemePreview } from "./purchasingThemePreview";
import "./PmsThemeSettingsPanel.css";

const DEMO_UX_MESSAGES = {
  en: {
    readOnly: "Demo · View only. Theme and language changes are disabled; no preferences are saved.",
    themes: "Available PMS themes. This demo keeps its current theme.",
    languages: "Available interface languages. This view keeps the visitor's current language.",
    preview: "Built-in visual example with generic fictional reservations. These are not Hotel Travesía's exported records.",
  },
  es: {
    readOnly: "Demo · Solo consulta. Los cambios de tema e idioma están deshabilitados; no se guardan preferencias.",
    themes: "Temas disponibles en el PMS. Esta demo mantiene su tema actual.",
    languages: "Idiomas disponibles para la interfaz. Esta vista mantiene el idioma actual del visitante.",
    preview: "Ejemplo visual integrado con reservas ficticias genéricas. No son los registros exportados del Hotel Travesía.",
  },
  ca: {
    readOnly: "Demo · Només consulta. Els canvis de tema i idioma estan deshabilitats; no es desen preferències.",
    themes: "Temes disponibles al PMS. Aquesta demo manté el tema actual.",
    languages: "Idiomes disponibles per a la interfície. Aquesta vista manté l'idioma actual del visitant.",
    preview: "Exemple visual integrat amb reserves fictícies genèriques. No són els registres exportats de l'Hotel Travesía.",
  },
};

const reservationPreviewRows = [
  {
    locator: "PMS-4312",
    guest: "Marina Torres",
    room: "Deluxe Sea View",
    status: "Confirmed",
    total: "EUR 512.00",
  },
  {
    locator: "PMS-4313",
    guest: "Oliver Bennett",
    room: "Junior Suite",
    status: "Requested",
    total: "EUR 684.00",
  },
  {
    locator: "PMS-4314",
    guest: "Sofia Rojas",
    room: "Superior Double",
    status: "Cancelled",
    total: "EUR 0.00",
  },
];

function PmsThemeSettingsPanel({ previewModule = "pms" }) {
  const demoModeActive = isDemoMode();
  const { themeId, themes, setThemeId, language, languageId, languages, setLanguageId } =
    usePmsTheme();
  const isLightTheme = themeId === "light";
  const purchasingPreview = previewModule === "purchasing" ? getPurchasingThemePreview(languageId) : null;
  const uxCopy = { ...language.messages.settings, ...purchasingPreview?.settings };
  const sidebarCopy = language.messages.sidebar;
  const reservationsCopy = language.messages.reservations;
  const tabsCopy = language.messages.reservationsTabs;
  const demoCopy = {
    ...(DEMO_UX_MESSAGES[languageId] || DEMO_UX_MESSAGES.en),
    ...(purchasingPreview ? { themes: purchasingPreview.demoThemes, preview: uxCopy.previewSubheading } : {}),
  };

  const sidebarPreviewItems = purchasingPreview?.sidebar || [
    sidebarCopy.dashboard,
    sidebarCopy.reservations,
    sidebarCopy.frontDesk,
    sidebarCopy.roomsManagement,
    sidebarCopy.settings,
  ];
  const previewTabs = purchasingPreview?.tabs || [tabsCopy.listOfReservations, tabsCopy.reservationsAvailability, tabsCopy.categorySalesControl];
  const previewColumns = purchasingPreview?.columns || [reservationsCopy.locator, reservationsCopy.customer, reservationsCopy.room, reservationsCopy.totalPrice, reservationsCopy.reservationStatus];
  const previewRows = purchasingPreview?.rows || reservationPreviewRows.map((row) => ({
    id: row.locator,
    cells: [row.locator, row.guest, row.room, row.total],
    status: translateReservationStatus(row.status, languageId),
    tone: row.status === "Confirmed" ? "confirmed" : row.status === "Requested" ? "requested" : "cancelled",
  }));
  return (
    <section
      className={`pmsThemePanel${demoModeActive ? " pmsThemePanel--demo" : ""}`}
      data-theme={isLightTheme ? "light" : "dark"}
      aria-labelledby="pms-ux-title"
    >
      <header className="pmsThemePanel__header">
        <div className="pmsThemePanel__eyebrow">{uxCopy.panelEyebrow}</div>
        <h1 className="pmsThemePanel__title" id="pms-ux-title">{uxCopy.panelTitle}</h1>
        <p className="pmsThemePanel__copy">{demoModeActive ? demoCopy.readOnly : uxCopy.panelCopy}</p>
        <p className="pmsThemePanel__note">{demoModeActive ? demoCopy.readOnly : uxCopy.note}</p>
      </header>

      <div className="pmsThemePanel__controls">
        <div className="pmsThemePanel__section pmsThemePanel__section--theme">
          <div className="pmsThemePanel__sectionEyebrow">{uxCopy.appearanceSection}</div>
          <h2 className="pmsThemePanel__sectionTitle" id="pms-ux-theme-title">{uxCopy.appearanceTitle}</h2>
          <p className="pmsThemePanel__sectionCopy">{demoModeActive ? demoCopy.themes : uxCopy.appearanceCopy}</p>

          <div className="pmsThemePanel__options" role="group" aria-labelledby="pms-ux-theme-title">
            {themes.map((option) => {
              const isActive = option.id === themeId;
              const ThemeIcon = option.id === "light" ? FiSun : FiMoon;
              const swatches = [
                option.preview.shell,
                option.preview.sidebar,
                option.preview.surface,
                option.preview.sidebarAccent,
                option.preview.highlight,
              ];

              return (
                <button
                  key={option.id}
                  type="button"
                  className={`pmsThemePanel__option ${isActive ? "pmsThemePanel__option--active" : ""}`}
                  disabled={demoModeActive}
                  title={demoModeActive ? demoCopy.readOnly : undefined}
                  aria-pressed={isActive}
                  onClick={() => {
                    if (demoModeActive || isDemoMode()) return;
                    setThemeId(option.id);
                  }}
                >
                  <div className="pmsThemePanel__swatches" aria-hidden="true">
                    {swatches.map((color, index) => (
                      <span
                        key={`${option.id}-swatch-${index}`}
                        className="pmsThemePanel__swatch"
                        style={{ background: color }}
                      />
                    ))}
                  </div>

                  <div className="pmsThemePanel__optionHead">
                    <div className="pmsThemePanel__optionTitle">
                      <ThemeIcon size={16} />
                      <span>{option.label}</span>
                    </div>
                    {isActive ? (
                      <span className="pmsThemePanel__check" aria-hidden="true">
                        <FiCheck size={14} />
                      </span>
                    ) : null}
                  </div>

                  <div className="pmsThemePanel__optionMeta">{option.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="pmsThemePanel__section pmsThemePanel__section--language">
          <div className="pmsThemePanel__sectionEyebrow">{uxCopy.languageSection}</div>
          <h2 className="pmsThemePanel__sectionTitle" id="pms-ux-language-title">{uxCopy.languageTitle}</h2>
          <p className="pmsThemePanel__sectionCopy">{demoModeActive ? demoCopy.languages : uxCopy.languageCopy}</p>

          <div className="pmsThemePanel__options" role="group" aria-labelledby="pms-ux-language-title">
            {languages.map((option) => {
              const isActive = option.id === languageId;

              return (
                <button
                  key={option.id}
                  type="button"
                  className={`pmsThemePanel__option ${isActive ? "pmsThemePanel__option--active" : ""}`}
                  disabled={demoModeActive}
                  title={demoModeActive ? demoCopy.readOnly : undefined}
                  aria-pressed={isActive}
                  onClick={() => {
                    if (demoModeActive || isDemoMode()) return;
                    setLanguageId(option.id);
                  }}
                >
                  <div className="pmsThemePanel__optionHead">
                    <div className="pmsThemePanel__optionTitle">
                      <span>{option.nativeLabel}</span>
                    </div>
                    {isActive ? (
                      <span className="pmsThemePanel__check" aria-hidden="true">
                        <FiCheck size={14} />
                      </span>
                    ) : null}
                  </div>

                  <div className="pmsThemePanel__optionCopy">
                    <div className="pmsThemePanel__optionMeta">{option.label}</div>
                    <div className="pmsThemePanel__optionTag">{option.locale}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {demoModeActive && <p className="pmsThemePanel__copy" id="pms-theme-demo-preview-note">{demoCopy.preview}</p>}
      <div className="pmsThemePanel__preview" aria-describedby={demoModeActive ? "pms-theme-demo-preview-note" : undefined}>
        <div className="pmsThemePanel__previewFrame">
          <aside className="pmsThemePanel__sidebar" aria-label={purchasingPreview?.sidebarLabel || "Sidebar preview"}>
            <div className="pmsThemePanel__sidebarBrand">
              <span className="pmsThemePanel__brandDot" aria-hidden="true" />
              <span>PILCORE</span>
            </div>

            <div className="pmsThemePanel__sidebarNav">
              {sidebarPreviewItems.map((item, index) => (
                <div
                  key={item}
                  className={`pmsThemePanel__sidebarItem ${
                    index === 1 ? "pmsThemePanel__sidebarItem--active" : ""
                  }`}
                >
                  {item}
                </div>
              ))}
            </div>
          </aside>

            <div className="pmsThemePanel__main">
              <div className="pmsThemePanel__tabs">
                {previewTabs.map((label, index) => (
                  <span key={label} className={`pmsThemePanel__tab${index === 0 ? " pmsThemePanel__tab--active" : ""}`}>{label}</span>
                ))}
              </div>

              <div className="pmsThemePanel__contentCard">
                <div className="pmsThemePanel__toolbar">
                  <div>
                    <div className="pmsThemePanel__heading">{purchasingPreview?.title || reservationsCopy.pageTitle}</div>
                    <div className="pmsThemePanel__subheading">{uxCopy.previewSubheading}</div>
                  </div>

                  <div className="pmsThemePanel__actions">
                    <span className="pmsThemePanel__pill pmsThemePanel__pill--primary">
                      {uxCopy.previewActionNew}
                    </span>
                    <span className="pmsThemePanel__pill">{uxCopy.previewActionExport}</span>
                    <span className="pmsThemePanel__pill pmsThemePanel__pill--success">
                      {uxCopy.previewActionListMode}
                    </span>
                  </div>
                </div>

              <div className="pmsThemePanel__tableScroll" tabIndex={0} role="region" aria-label={uxCopy.previewSubheading}>
              <table className="pmsThemePanel__table">
                <thead>
                  <tr>
                    {previewColumns.map((label) => <th key={label} scope="col">{label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row) => (
                      <tr key={row.id}>
                        {row.cells.map((cell, index) => <td key={index} className={index === 3 ? "pmsThemePanel__total" : undefined}>{cell}</td>)}
                        <td>
                          <span className={`pmsThemePanel__status pmsThemePanel__status--${row.tone}`}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}

export default PmsThemeSettingsPanel;
